import { Observable, of, ReplaySubject, Subject } from "rxjs";
import { SavedQuery, SearchRangeRequest } from "@app2/type-defs/search/search-types";
import { SearchClientService } from "@app2/clients/search-client.service";
import { Injectable } from "@angular/core";
import { map, mergeMap, throttleTime } from "rxjs/operators";
import { localization } from "@app2/shared/localization/localization";
import { pickBy } from "lodash-es";
import { TimeService } from "@app2/shared/services/time.service";
import * as _ from "underscore";

const NONEXISTENT_SAVED_QUERY: SavedQuery = {
    beta: false,
    deleted: false,
    orgId: null,
    libraryQueryId: null,
    savedQueryId: null,
    concatType: null,
    unmodifiable: false,
    createdBy: "",
    createdOn: "",
    id: "",
    modifiedBy: "",
    modifiedOn: "",
    name: localization.getString("NONEXISTENT_SAVED_QUERY_NAME"),
    description: "",
    query: "",
    baseQuery: null,
};

const averageOverLastDays = 3;

@Injectable({
    providedIn: "root",
})
export class SavedQueryService {
    private savedQueries$ = new ReplaySubject<Record<uuid, SavedQuery>>(1);
    private savedQueries: Record<uuid, SavedQuery> = {};

    private requestReload = new Subject<void>();

    constructor(readonly searchService: SearchClientService,
                readonly timeService: TimeService) {
        this.reloadSavedQueries();

        this.requestReload
            .pipe(
                throttleTime(1000),
            )
            .subscribe(() => this.reloadSavedQueries());
    }

    public reloadSavedQueries(): void {
        this.searchService.getAllSavedQueries(true, true)
            .then((savedQueries: SavedQuery[]) => {
                this.savedQueries = _.indexBy(savedQueries, "id");
                this.savedQueries$.next(this.savedQueries);
            });
    }

    public updateSavedQuery(savedQuery: SavedQuery): void {
        this.savedQueries[savedQuery.id] = savedQuery;
        this.savedQueries$.next(this.savedQueries);
    }

    public getSavedQueries$(reload = false): Observable<Record<uuid, SavedQuery>> {
        if (reload) {
            this.requestReload.next();
        }
        return this.savedQueries$.asObservable();
    }

    public getSavedQuery$(id: uuid): Observable<SavedQuery> {
        return this.getSavedQueries$()
            .pipe(
                mergeMap(savedQueries => id in savedQueries
                    ? of(savedQueries[id])
                    : this.getSavedQueries$(true)
                        .pipe(
                            map(savedQueries => savedQueries[id] ?? NONEXISTENT_SAVED_QUERY),
                        ),
                ),
            );
    }

    public getActiveSavedQueries$(reload = false): Observable<Record<uuid, SavedQuery>> {
        return this.getSavedQueries$(reload)
            .pipe(
                map(savedQueries => pickBy(savedQueries, savedQuery => !savedQuery.deleted)),
            );
    }

    public getAverageDailyMatches$(query: string): Observable<number> {
        const requestParams: SearchRangeRequest = {
            queryString: query,
            begin: this.timeService.now().subtract(averageOverLastDays, "d").toISOString(),
            end: this.timeService.now().toISOString(),
            pageNumber: 1,
            pageSize: 100,
            sortField: "timestamp",
            sortDesc: true,
        };

        return this.searchService.searchByRange$(requestParams)
            .pipe(
                map(searchRange => Math.floor(searchRange.totalHits / averageOverLastDays)),
            );
    }
}
